const BASE_URL = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('ozon_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem('ozon_token');
    window.location.href = '/login';
    throw new Error('Oturum sona erdi');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'API hatası');
  }
  return res.json();
}

export const api = {
  login: (username, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),

  getOrders: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/orders${q ? `?${q}` : ''}`);
  },
  getOrderCounts: () => request('/orders/counts'),
  getOrderStats: () => request('/orders/stats'),
  getGroupedOrders: () => request('/orders/grouped'),
  getOrder: (id) => request(`/orders/${id}`),
  getOrderEvents: (id) => request(`/orders/${encodeURIComponent(id)}/events`),
  syncOrders: () => request('/orders/sync', { method: 'POST' }),
  getSyncStatus: () => request('/orders/sync-status'),
  printOrder: (id, type) =>
    request(`/orders/${id}/print`, { method: 'POST', body: JSON.stringify({ type }) }),

  getProducts: () => request('/products'),
  updateStock: (offerId, stock_quantity, note) =>
    request(`/products/${encodeURIComponent(offerId)}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ stock_quantity, note }),
    }),

  // Etiketler
  getLabels: () => request('/labels'),
  createLabel: (name, color) => request('/labels', { method: 'POST', body: JSON.stringify({ name, color }) }),
  updateLabel: (id, data) => request(`/labels/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLabel: (id) => request(`/labels/${id}`, { method: 'DELETE' }),
  assignLabel: (labelId, posting_number) =>
    request(`/labels/${labelId}/assign`, { method: 'POST', body: JSON.stringify({ posting_number }) }),
  unassignLabel: (labelId, posting_number) =>
    request(`/labels/${labelId}/unassign`, { method: 'DELETE', body: JSON.stringify({ posting_number }) }),
  bulkAssignLabel: (labelId, posting_numbers) =>
    request(`/labels/${labelId}/assign-bulk`, { method: 'POST', body: JSON.stringify({ posting_numbers }) }),

  // Toplu packing slip — blob döndürür
  bulkPackingSlip: async (posting_numbers) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/bulk-packing-slip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ posting_numbers }),
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'PDF hatası'); }
    return res.blob();
  },

  // Sevkiyata hazırla → ready_to_ship (etiket arka planda kuyruğa girer)
  shipAndLabel: (posting_number) =>
    request(`/orders/${posting_number}/ship-and-label`, { method: 'POST' }),

  // Geri gönder: ready_to_ship → awaiting_packaging (DB-only)
  sendBack: (posting_number) =>
    request(`/orders/${posting_number}/send-back`, { method: 'POST' }),
  bulkSendBack: (posting_numbers) =>
    request('/orders/bulk-send-back', { method: 'POST', body: JSON.stringify({ posting_numbers }) }),

  // Tedarik Bekliyor akışı
  // expect: 'awaiting_deliver' (tedarike ayır) | 'awaiting_supply' (kargoya okut)
  resolveScan: (code, expect) =>
    request('/orders/resolve-scan', { method: 'POST', body: JSON.stringify({ code, expect }) }),
  bulkAwaitingSupply: (posting_numbers, note) =>
    request('/orders/bulk-awaiting-supply', { method: 'POST', body: JSON.stringify({ posting_numbers, note }) }),
  bulkSupplyGeri: (posting_numbers) =>
    request('/orders/bulk-supply-geri', { method: 'POST', body: JSON.stringify({ posting_numbers }) }),
  // from: 'awaiting_deliver' | 'awaiting_supply' — hangi sekmeden basıldığı
  bulkKargoHazir: (posting_numbers, from) =>
    request('/orders/bulk-kargo-hazir', { method: 'POST', body: JSON.stringify({ posting_numbers, from }) }),

  // Analitik
  getAnalytics: (period = 30) => request(`/analytics?period=${period}`),

  // Uygulama ayarları
  getSettings: () => request('/settings'),
  saveSettings: (data) => request('/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Karlılık analizi
  getProfitability: () => request('/profitability'),
  getProductCosts: (offerId) => request(`/profitability/${encodeURIComponent(offerId)}/costs`),
  addProductCost: (offerId, data) =>
    request(`/profitability/${encodeURIComponent(offerId)}/costs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateProductCost: (id, data) =>
    request(`/profitability/costs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProductCost: (id) =>
    request(`/profitability/costs/${id}`, { method: 'DELETE' }),
  importCostsCsv: (csv) =>
    request('/profitability/import-csv', { method: 'POST', body: JSON.stringify({ csv }) }),

  // Finansal detay (Ozon financial_data)
  getFinancials: (period = 30) => request(`/financials?period=${period}`),

  // Canlı döviz kuru (USD/RUB)
  getExchangeRate: () => request('/exchange-rate'),

  // Etiket kuyruk durumu: { [posting_number]: 'none'|'pending'|'ready'|'failed' }
  getLabelQueue: () => request('/orders/label-queue'),

  // Hatalı etiket olan tüm siparişler (statüden bağımsız)
  getLabelFailures: () => request('/orders/label-failures'),

  // Tekil etiketi (yeniden) kuyruğa ekle
  enqueueLabel: (posting_number) =>
    request(`/orders/${encodeURIComponent(posting_number)}/enqueue-label`, { method: 'POST' }),

  // Toplu kuyruğa ekle
  bulkEnqueueLabel: async (posting_numbers) =>
    request('/orders/bulk-enqueue-label', { method: 'POST', body: JSON.stringify({ posting_numbers }) }),

  // Etiket PDF'i indir (Blob)
  downloadLabel: async (posting_number) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/${posting_number}/label.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Etiket alınamadı'); }
    return res.blob();
  },

  // Toplu Ozon etiketi — blob döndürür
  bulkLabel: async (posting_numbers) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/bulk-label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ posting_numbers }),
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Etiket PDF hatası'); }
    return res.blob();
  },

  // Paketçi kontrol listesi — sıralı tablo PDF
  pickingChecklist: async (posting_numbers) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/picking-checklist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ posting_numbers }),
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Kontrol listesi hatası'); }
    return res.blob();
  },

  // Sıralı etiket: her sipariş için [bilgi kartı + Ozon etiketi] interleaved PDF
  // update_status=true → ready_to_ship → awaiting_deliver
  interleavedLabel: async (posting_numbers, update_status = false) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/interleaved-label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ posting_numbers, update_status }),
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Sıralı etiket hatası'); }
    return res.blob();
  },

  // Gelişmiş etiket: Ozon label + altta offer_id & adet şeridi
  // update_status=true → basılan siparişleri ready_to_ship → awaiting_deliver'a taşır
  enhancedLabel: async (posting_numbers, update_status = false) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/orders/enhanced-label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ posting_numbers, update_status }),
    });
    if (res.status === 401) { localStorage.removeItem('ozon_token'); window.location.href = '/login'; throw new Error('Oturum sona erdi'); }
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Gelişmiş etiket hatası'); }
    return res.blob();
  },
};
